
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";


// Helper function to create match notification
async function createMatchNotification(supabase: any, jobId: string, candidateId: string, matchId: string) {
    try {
        // Get job and candidate data for notification
        const { data: jobData } = await supabase
            .from('jobs')
            .select('title, organization_uuid')
            .eq('id', jobId)
            .single();

        const { data: candidateData } = await supabase
            .from('candidates')
            .select('name')
            .eq('id', candidateId)
            .single();

        if (!jobData || !candidateData) {
            console.error('Job or candidate data not found for notification');
            return;
        }

        // Create notification using subscription system with enhanced message
        await supabase.rpc('create_org_notifications_with_subscriptions', {
            p_organization_uuid: jobData.organization_uuid,
            p_type: 'new_application',
            p_title: `New Match: ${candidateData.name} → ${jobData.title}`,
            p_message: `A new match has been created between "${candidateData.name}" and the "${jobData.title}" position.`,
            p_data: {
                entity_type: 'match',
                entity_id: matchId,
                job_id: jobId,
                candidate_id: candidateId,
                job_title: jobData.title,
                candidate_name: candidateData.name,
                action: 'created'
            },
            p_entity_type: 'match',
            p_entity_id: matchId
        });

        console.log('Created match notification using subscription system');
    } catch (error) {
        console.error('Error creating match notification:', error);
    }
}

// Helper function to create status change notification
async function createStatusChangeNotification(supabase: any, matchId: string, newStage: string) {
    try {
        // Get match details with job and candidate info
        const { data: matchData } = await supabase
            .from('matches')
            .select(`
                id,
                status,
                jobs(title, organization_uuid),
                candidates(name)
            `)
            .eq('id', matchId)
            .single();

        if (!matchData || !matchData.jobs || !matchData.candidates) {
            console.error('Match data not found for notification:', matchId);
            return;
        }

        // Create enhanced status change message
        const statusMessage = `${matchData.candidates.name} has been moved from "${matchData.status || 'No Status'}" to "${newStage}" for the position "${matchData.jobs.title}".`;

        // Create notification using subscription system with enhanced message
        await supabase.rpc('create_org_notifications_with_subscriptions', {
            p_organization_uuid: matchData.jobs.organization_uuid,
            p_type: 'application_status_change',
            p_title: `Status Changed: ${matchData.candidates.name} → ${newStage}`,
            p_message: statusMessage,
            p_data: {
                entity_type: 'match',
                entity_id: matchId,
                job_id: matchData.jobs.id,
                candidate_id: matchData.candidates.id,
                job_title: matchData.jobs.title,
                candidate_name: matchData.candidates.name,
                new_status: newStage,
                previous_status: matchData.status,
                action: 'status_changed'
            },
            p_entity_type: 'match',
            p_entity_id: matchId
        });

        console.log('Created status change notification using subscription system');
    } catch (error) {
        console.error('Error creating status change notification:', error);
    }
}

// Fetch all candidates for a specific job
export const fetchCandidatesForJob = async (jobId: string) => {
    try {
        // Fetch matches with candidates for this job
        const { data, error } = await supabase
            .from('matches')
            .select(`
        id,
        candidate_id,
        job_id,
        score,
        status,
        created_at,
        candidates(
          id,
          name,
          email,
          skills,
          function
        )
      `)
            .eq('job_id', jobId);

        if (error) {
            throw error;
        }

        // Transform the data for kanban display
        return data?.map(match => ({
            id: match.id,
            candidateId: match.candidate_id,
            jobId: match.job_id,
            stage: match.status || 'matched', // Default to matched if no status
            name: match.candidates?.name || 'Unknown',
            email: match.candidates?.email || '',
            skills: match.candidates?.skills || [],
            position: match.candidates?.function || '',
            score: match.score || 0,
            createdAt: match.created_at
        })) || [];

    } catch (error) {
        console.error('Error fetching kanban data:', error);
        toast({
            title: "Error",
            description: "Failed to load recruitment process data",
            variant: "destructive",
        });
        return [];
    }
};

// Update a candidate's stage in the recruitment process
export const updateCandidateStage = async (matchId: string, newStage: string) => {
    try {
        // Fetch current match to get old status and org (via job)
        const { data: match, error: fetchError } = await supabase
            .from('matches')
            .select('status, jobs(organization_uuid)')
            .eq('id', matchId)
            .single();

        if (fetchError) throw fetchError;

        const fromStatus = match?.status ?? null;
        const organizationUuid = (match?.jobs as any)?.organization_uuid;

        // Update the match status in the database
        const { error } = await supabase
            .from('matches')
            .update({ status: newStage })
            .eq('id', matchId);

        if (error) throw error;

        // Create notification for status change
        await createStatusChangeNotification(supabase, matchId, newStage);

        // Trigger status transition email (fire-and-forget)
        if (organizationUuid)
            supabase.functions.invoke('handle-status-transition', {
                body: {
                    entityType: 'matches',
                    entityId: matchId,
                    fromStatus,
                    toStatus: newStage,
                    organizationUuid,
                },
            }).then(({ data, error }) => {
                if (error) console.error('handle-status-transition error:', error);
                else console.info('handle-status-transition response:', data);
            }).catch((err) => console.error('handle-status-transition invoke failed:', err));

        toast({
            title: "Success",
            description: `Candidate moved to ${newStage} stage`,
        });

        return true;
    } catch (error) {
        console.error('Error updating candidate stage:', error);
        toast({
            title: "Error",
            description: "Failed to update candidate stage",
            variant: "destructive",
        });
        return false;
    }
};

// Add a candidate to a job
export const addCandidateToJob = async (jobId: string, candidateId: string) => {
    try {
        // Check if the candidate already exists in this job
        const { data: existingMatch } = await supabase
            .from('matches')
            .select('id')
            .eq('job_id', jobId)
            .eq('candidate_id', candidateId)
            .single();

        if (existingMatch) {
            toast({
                title: "Warning",
                description: "This candidate is already in the recruitment process",
            });
            return false;
        }

        // Add candidate to job with initial stage "applied"
        const { data, error } = await supabase
            .from('matches')
            .insert({
                job_id: jobId,
                candidate_id: candidateId,
                score: 0, // Initial score, will be calculated later
                status: 'matched'
            })
            .select('id'); // Return the new match ID

        if (error) throw error;

        if (!data || data.length === 0) {
            throw new Error("Failed to add candidate to job");
        }

        // also trigger a call to ai-processing
        await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
            method: 'POST',
            body: {
                entityId: data[0].id, // Use the match ID as entityId
                entityType: "match",
                analysisType: "full-match",
                actionOnComplete: "update-match",
            }
        });

        // Create notification for new match
        await createMatchNotification(supabase, jobId, candidateId, data[0].id);

        //console.log("AI analysis triggered for new match:", data[0].id);
        // Notify the user of success


        toast({
            title: "Success",
            description: "AI analysis triggered for new match",
        });

        return true;
    } catch (error) {
        console.error('Error adding candidate to job:', error);
        toast({
            title: "Error",
            description: "Failed to add candidate to recruitment process",
            variant: "destructive",
        });
        return false;
    }
};
